import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Save,
  Person,
  Business,
  Phone,
  Email,
  LocationOn,
  AccountBalance,
  Edit,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { vendorAPI } from 'services/api';

const VendorProfileSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [profile, setProfile] = useState({
    business_name: '',
    contact_person: '',
    phone: '',
    email: '',
    gst_number: '',
    pan_number: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    bank_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_branch: '',
  });

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await vendorAPI.getProfile();
      if (response.data) {
        setProfile((prev) => ({
          ...prev,
          ...response.data,
        }));
      }
    } catch (err) {
      console.error('Failed to load vendor profile:', err);
      // Populate from user if profile fetch fails
      setProfile((prev) => ({
        ...prev,
        contact_person: user?.name || '',
        phone: user?.phone || '',
        email: user?.email || '',
      }));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleChange = (field) => (e) => {
    setProfile((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      await vendorAPI.updateProfile(profile);
      setSuccess('Profile updated successfully!');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update profile.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          ⚙️ Profile Settings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your business information, contact details, and banking settings.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Profile Header Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: '#1976D2',
                  fontSize: '2rem',
                }}
              >
                {(profile.business_name || user?.name || 'V').charAt(0).toUpperCase()}
              </Avatar>
              <IconButton
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -4,
                  bgcolor: 'white',
                  boxShadow: 1,
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {profile.business_name || 'Your Business'}
              </Typography>
              <Typography color="text.secondary">
                {profile.email || user?.email} • {profile.phone || user?.phone}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Vendor ID: {user?.id || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Business Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Business sx={{ color: '#1976D2', mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Business Information
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Business Name"
                fullWidth
                value={profile.business_name}
                onChange={handleChange('business_name')}
                InputProps={{
                  startAdornment: <Business sx={{ color: '#9e9e9e', mr: 1 }} fontSize="small" />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="GST Number"
                fullWidth
                value={profile.gst_number}
                onChange={handleChange('gst_number')}
                placeholder="e.g., 22AAAAA0000A1Z5"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="PAN Number"
                fullWidth
                value={profile.pan_number}
                onChange={handleChange('pan_number')}
                placeholder="e.g., ABCDE1234F"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Contact Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Person sx={{ color: '#4CAF50', mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Contact Details
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Person"
                fullWidth
                value={profile.contact_person}
                onChange={handleChange('contact_person')}
                InputProps={{
                  startAdornment: <Person sx={{ color: '#9e9e9e', mr: 1 }} fontSize="small" />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                fullWidth
                value={profile.phone}
                onChange={handleChange('phone')}
                InputProps={{
                  startAdornment: <Phone sx={{ color: '#9e9e9e', mr: 1 }} fontSize="small" />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={profile.email}
                onChange={handleChange('email')}
                InputProps={{
                  startAdornment: <Email sx={{ color: '#9e9e9e', mr: 1 }} fontSize="small" />,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Address */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <LocationOn sx={{ color: '#FF9800', mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Address
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label="Full Address"
                fullWidth
                multiline
                rows={2}
                value={profile.address}
                onChange={handleChange('address')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="City"
                fullWidth
                value={profile.city}
                onChange={handleChange('city')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="State"
                fullWidth
                value={profile.state}
                onChange={handleChange('state')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Pincode"
                fullWidth
                value={profile.pincode}
                onChange={handleChange('pincode')}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Banking Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AccountBalance sx={{ color: '#9C27B0', mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Banking Details
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            Bank details are required for receiving payments. Ensure they are accurate.
          </Alert>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Bank Name"
                fullWidth
                value={profile.bank_name}
                onChange={handleChange('bank_name')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Account Number"
                fullWidth
                value={profile.bank_account_number}
                onChange={handleChange('bank_account_number')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="IFSC Code"
                fullWidth
                value={profile.bank_ifsc}
                onChange={handleChange('bank_ifsc')}
                placeholder="e.g., SBIN0001234"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Branch"
                fullWidth
                value={profile.bank_branch}
                onChange={handleChange('bank_branch')}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* Save */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={loadProfile} disabled={saving}>
          Reset
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={16} /> : <Save />}
          onClick={handleSave}
          disabled={saving}
          sx={{ minWidth: 160 }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
};

export default VendorProfileSettings;
